import React, { useEffect, useState } from "react";
import { Gift, Save, Settings2, Users, Wallet } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";
import WhatsAppGlyph from "../components/WhatsAppGlyph";

const AVIATOR_RATIO_LABELS = [
  "1.01-1.09",
  "1.10-2",
  "2.01-5",
  "5.01-10",
  "10.01-20",
  "20.01-50",
  "50.01-100",
  "100.01-220",
  "220.01-500",
];

function createDefaultAviatorProfiles() {
  return [
    { id: "row_1", name: "Row 1", weights: [16, 40, 20, 12, 7, 2.7, 1.1, 0.5, 0.5] },
  ];
}

function parseStoredAviatorProfiles(rawProfiles, rawFallbackWeights) {
  const fallback = parseAviatorRatioDraft(rawFallbackWeights || currentRatioDefault());
  try {
    const parsed = JSON.parse(String(rawProfiles || "[]"));
    if (!Array.isArray(parsed) || !parsed.length) throw new Error("empty");
    const next = parsed.map((item, index) => ({
      id: String(item?.id || `row_${index + 1}`),
      name: String(item?.name || `Row ${index + 1}`),
      weights: normalizeAviatorProfileWeights(Array.isArray(item?.weights) ? item.weights : parseAviatorRatioDraft(item?.weights || ""), fallback),
    }));
    return next.length ? next : createDefaultAviatorProfiles();
  } catch {
    return [
      { id: "row_1", name: "Row 1", weights: fallback },
    ];
  }
}

function parseAviatorRatioDraft(value = "") {
  return String(value || "")
    .split(/[,\n\r]+/)
    .map((item) => Number.parseFloat(String(item).trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
}

function normalizeAviatorProfileWeights(weights, fallback = parseAviatorRatioDraft(currentRatioDefault())) {
  const values = Array.isArray(weights)
    ? weights.map((item) => Number.parseFloat(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  return values.length === AVIATOR_RATIO_LABELS.length ? values : [...fallback];
}

const ADMIN_PAGE_CHOICES = [
  { path: "/admin", label: "Dashboard" },
  { path: "/admin/users", label: "Users" },
  { path: "/admin/deposits", label: "Deposits" },
  { path: "/admin/payouts", label: "Payouts" },
  { path: "/admin/pnl-statement", label: "P&L Statement" },
  { path: "/admin/expenses", label: "Expenses" },
  { path: "/admin/referral-bonuses", label: "Referral Bonus" },
  { path: "/admin/results", label: "Market Outcome" },
  { path: "/admin/auto-results", label: "Auto Result Demo" },
  { path: "/admin/markets", label: "Game Management" },
  { path: "/admin/charts", label: "Chart Upload" },
  { path: "/admin/banners", label: "Banners" },
  { path: "/admin/payment-gateway", label: "Payment Gateway" },
  { path: "/admin/notifications", label: "Send Notifications" },
  { path: "/admin/agents", label: "Agent Program" },
  { path: "/admin/casino", label: "MK Casino" },
  { path: "/admin/promotions", label: "Promotions" },
  { path: "/admin/settings", label: "Settings" },
  { path: "/admin/coding", label: "Coding" },
  { path: "/admin/google-auth", label: "Google Auth" },
  { path: "/admin/sub-admins", label: "Sub Admins" },
  { path: "/admin/starline/declare", label: "Starline" },
];

function AdminSettings() {
  const admin = (() => {
    try {
      return JSON.parse(localStorage.getItem("admin") || "null");
    } catch {
      return null;
    }
  })();
  const [form, setForm] = useState({
    min_bet: "10",
    max_bet: "10000",
    min_deposit: "300",
    min_withdrawal: "500",
    signup_bonus: "0",
    withdraw_limit_per_day: "1",
    whatsapp_number: "",
    deposit_qr_url: "/qr.png",
    deposit_mode: "qr",
    deposit_bank_name: "",
    deposit_account_name: "",
    deposit_account_number: "",
    deposit_ifsc: "",
    deposit_upi_id: "",
    referral_commission_percent: "3",
    referral_claim_min: "100",
    default_ank_limit: "",
    aviator_enabled: "1",
    aviator_history_limit: "40",
    aviator_max_round_limit: "500",
    aviator_ratio_weights: "16,40,20,12,7,2.7,1.1,0.5,0.5",
    aviator_ratio_profiles: '[{"id":"row_1","name":"Row 1","weights":[16,40,20,12,7,2.7,1.1,0.5,0.5]}]',
    aviator_active_ratio_profile: "row_1",
    aviator_ratio_switch_mode: "manual",
    aviator_ratio_switch_minutes: "10",
    app_download_url: "",
    app_download_label: "Download App Now",
    home_running_notice: "",
    android_app_latest_version: "",
    android_app_update_message: "A new MK Games update is available.",
    android_app_force_update: "0",
  });
  const [aviatorProfiles, setAviatorProfiles] = useState(createDefaultAviatorProfiles());
  const [activeAviatorRatioProfile, setActiveAviatorRatioProfile] = useState("row_1");
  const [, setAviatorRatioCheck] = useState({ ok: true, message: "" });
  const [storageStats, setStorageStats] = useState({ total_deposit_requests: 0, total_deposit_screenshots: 0 });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pageOrderKeys, setPageOrderKeys] = useState(ADMIN_PAGE_CHOICES.map((item) => item.path));

  useEffect(() => {
    API.get("/admin/settings")
      .then((res) => {
        const data = res.data || {};
        setForm((current) => ({ ...current, ...data }));
        const profiles = parseStoredAviatorProfiles(data.aviator_ratio_profiles, data.aviator_ratio_weights || currentRatioDefault());
        setAviatorProfiles(profiles);
        setActiveAviatorRatioProfile(String(data.aviator_active_ratio_profile || profiles[0]?.id || "row_1"));
        setAviatorRatioCheck({ ok: true, message: "" });
        const savedOrder = String(data.admin_page_order || "").trim().split(",").map((item) => item.trim()).filter(Boolean);
        if (savedOrder.length) {
          const remainder = ADMIN_PAGE_CHOICES.map((item) => item.path).filter((path) => !savedOrder.includes(path));
          setPageOrderKeys([...savedOrder.filter((path) => ADMIN_PAGE_CHOICES.some((item) => item.path === path)), ...remainder]);
        }
      })
      .catch(() => {});
    API.get("/admin/system/storage-stats")
      .then((res) => setStorageStats(res.data || { total_deposit_requests: 0, total_deposit_screenshots: 0 }))
      .catch(() => {});
  }, []);

  const saveSettings = async () => {
    const activeProfile = aviatorProfiles.find((item) => item.id === activeAviatorRatioProfile) || aviatorProfiles[0];
    const ratioValidation = validateAviatorRatioValues(activeProfile?.weights || []);
    setAviatorRatioCheck(ratioValidation);
    if (!ratioValidation.ok) {
      alert(ratioValidation.message);
      return;
    }
    const serializedProfiles = aviatorProfiles.map((item) => {
      const validated = validateAviatorRatioValues(item.weights || []);
      return {
        id: item.id,
        name: item.name || "Row",
        weights: validated.ok ? validated.values : normalizeAviatorProfileWeights(item.weights || []),
      };
    });
    try {
      await API.post("/admin/settings", {
        ...form,
        aviator_ratio_profiles: JSON.stringify(serializedProfiles),
        aviator_active_ratio_profile: activeProfile?.id || "row_1",
        aviator_ratio_weights: ratioValidation.values.join(","),
        admin_page_order: pageOrderKeys.join(",")
      });
      alert("Settings saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save settings");
    }
  };

  const changePassword = async () => {
    if (!admin?.session_token) {
      alert("Admin session not found");
      return;
    }
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      alert("Fill all password fields");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert("New password and confirm password do not match");
      return;
    }
    try {
      await API.post("/admin/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      alert("Admin password changed");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to change password");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Settings</h1>
            <p style={subtitle}>Platform configuration</p>
          </div>
          <button type="button" style={saveButton} onClick={saveSettings}>
            <Save size={18} /> Save
          </button>
        </div>

        <section style={heroCard}>
          <div style={heroHeader}>
            <strong style={heroTitle}>Platform Status</strong>
            <span style={liveBadge}>Live</span>
          </div>
          <div style={heroGrid}>
            <Metric title="Min Bet" value={`Rs ${form.min_bet || 0}`} icon={<Settings2 size={18} />} />
            <Metric title="Max Bet" value={`Rs ${form.max_bet || 0}`} icon={<Settings2 size={18} />} />
            <Metric title="Min Deposit" value={`Rs ${form.min_deposit || 0}`} icon={<Wallet size={18} />} />
            <Metric title="Min Withdrawal" value={`Rs ${form.min_withdrawal || 0}`} icon={<Wallet size={18} />} />
            <Metric title="Withdraw Limit" value={form.withdraw_limit_per_day === "0" ? "No limit" : `${form.withdraw_limit_per_day || 1} / day`} icon={<Wallet size={18} />} />
            <Metric title="Signup Bonus" value={`Rs ${form.signup_bonus || 0}`} icon={<Gift size={18} />} />
            <Metric title="Referral %" value={`${form.referral_commission_percent || 0}%`} icon={<Users size={18} />} />
            <Metric title="Global Ank" value={form.default_ank_limit ? `${form.default_ank_limit} digits` : "Off"} icon={<Settings2 size={18} />} />
            <Metric title="Aviator" value={String(form.aviator_enabled) === "1" ? "On" : "Off"} icon={<Settings2 size={18} />} />
            <Metric title="Shots" value={String(storageStats.total_deposit_screenshots || 0)} icon={<Wallet size={18} />} />
          </div>
        </section>

        <div style={grid}>
        <section style={card}>
          <h3 style={cardTitle}>Betting Limits</h3>
          <Field label="Min Bet Amount" value={form.min_bet} onChange={(value) => setForm((c) => ({ ...c, min_bet: value }))} />
          <Field label="Max Bet Amount" value={form.max_bet} onChange={(value) => setForm((c) => ({ ...c, max_bet: value }))} />
        </section>

        <section style={card}>
          <h3 style={cardTitle}>Coding</h3>
          <p style={helperText}>Aviator round setup, admin page order, date cleanup, logos, payment API notes and OTP API notes are now on one separate control page.</p>
          <a href="/admin/coding" style={heavySettingsLink}>Open Coding</a>
        </section>

          <section style={card}>
            <h3 style={cardTitle}>Transaction Limits</h3>
            <Field label="Min Deposit" value={form.min_deposit} onChange={(value) => setForm((c) => ({ ...c, min_deposit: value }))} />
            <Field label="Min Withdrawal" value={form.min_withdrawal} onChange={(value) => setForm((c) => ({ ...c, min_withdrawal: value }))} />
            <Field label="Global Default Ank Limit" value={form.default_ank_limit} onChange={(value) => setForm((c) => ({ ...c, default_ank_limit: value.replace(/[^\d]/g, "").slice(0, 4) }))} />
            <FieldSelect
              label="Withdraw Limit"
              value={form.withdraw_limit_per_day}
              onChange={(value) => setForm((c) => ({ ...c, withdraw_limit_per_day: value }))}
              options={[
                { value: "1", label: "Once per day" },
                { value: "2", label: "Twice per day" },
                { value: "0", label: "No limit" },
              ]}
            />
            <Field label="Signup Bonus" value={form.signup_bonus} onChange={(value) => setForm((c) => ({ ...c, signup_bonus: value }))} />
            <p style={helperText}>Leave global ank empty or `0` to keep ank restriction off by default. User-level ank on profile page will override this value.</p>
          </section>
        </div>

        <section style={card}>
          <h3 style={cardTitle}>Support WhatsApp Button</h3>
          <div style={supportPreview}>
            <span style={supportPreviewIcon}>
              <WhatsAppGlyph size={28} color="#ffffff" />
            </span>
            <div>
              <strong>Transparent glyph button</strong>
              <p style={supportHint}>Users will open direct WhatsApp chat from sidebar, dashboard, profile and deposit support buttons.</p>
            </div>
          </div>
          <Field label="WhatsApp Support Number" value={form.whatsapp_number} onChange={(value) => setForm((c) => ({ ...c, whatsapp_number: value }))} />
          <p style={supportHint}>Enter 10 digit number or full 91 number. Example: 9116489589 or 919116489589.</p>
        </section>

        <section style={card}>
          <h3 style={cardTitle}>App Download And Update</h3>
          <Field label="APK / Download Link" value={form.app_download_url} onChange={(value) => setForm((c) => ({ ...c, app_download_url: value }))} />
          <Field label="Button Label" value={form.app_download_label} onChange={(value) => setForm((c) => ({ ...c, app_download_label: value }))} />
          <Field label="Running Notice Text" value={form.home_running_notice} onChange={(value) => setForm((c) => ({ ...c, home_running_notice: value }))} />
          <Field label="Latest Android App Version" value={form.android_app_latest_version} onChange={(value) => setForm((c) => ({ ...c, android_app_latest_version: value }))} />
          <Field label="Update Message" value={form.android_app_update_message} onChange={(value) => setForm((c) => ({ ...c, android_app_update_message: value }))} />
          <FieldSelect
            label="Force Update In App"
            value={String(form.android_app_force_update || "0")}
            onChange={(value) => setForm((c) => ({ ...c, android_app_force_update: value }))}
            options={[
              { value: "0", label: "No" },
              { value: "1", label: "Yes" },
            ]}
          />
          <p style={helperText}>This link is shown on home page and dashboard for users who are not inside the app webview.</p>
          <p style={helperText}>If Latest Android App Version is higher than the installed shell version, the app will show an update button.</p>
        </section>

        <section style={card}>
          <h3 style={cardTitle}>Referral Program</h3>
          <div style={grid}>
            <Field
              label="Referral Commission %"
              value={form.referral_commission_percent}
              onChange={(value) => setForm((c) => ({ ...c, referral_commission_percent: value }))}
            />
            <Field
              label="Minimum Claim Amount"
              value={form.referral_claim_min}
              onChange={(value) => setForm((c) => ({ ...c, referral_claim_min: value }))}
            />
          </div>
        </section>

        <section style={card}>
          <h3 style={cardTitle}>Admin Password</h3>
          <Field label="Current Password" type="password" value={passwordForm.current_password} onChange={(value) => setPasswordForm((c) => ({ ...c, current_password: value }))} />
          <div style={grid}>
            <Field label="New Password" type="password" value={passwordForm.new_password} onChange={(value) => setPasswordForm((c) => ({ ...c, new_password: value }))} />
            <Field label="Confirm Password" type="password" value={passwordForm.confirm_password} onChange={(value) => setPasswordForm((c) => ({ ...c, confirm_password: value }))} />
          </div>
          <button type="button" style={passwordButton} onClick={changePassword}>Change Admin Password</button>
        </section>

      </section>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <input type={type} style={fieldInput} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function currentRatioDefault() {
  return "16,40,20,12,7,2.7,1.1,0.5,0.5";
}

function validateAviatorRatioValues(rawValues) {
  const values = Array.isArray(rawValues)
    ? rawValues.map((item) => Number.parseFloat(item)).filter((item) => Number.isFinite(item) && item > 0)
    : [];
  if (values.length !== AVIATOR_RATIO_LABELS.length) {
    return {
      ok: false,
      message: `Need exactly ${AVIATOR_RATIO_LABELS.length} weights. Current: ${values.length}`,
      values,
    };
  }
  if (values.some((item) => item <= 0)) {
    return {
      ok: false,
      message: "All Aviator ratio weights must be greater than 0",
      values,
    };
  }
  return {
    ok: true,
    message: `Valid ratio for ${AVIATOR_RATIO_LABELS.length} crash tiers`,
    values,
  };
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <select style={fieldInput} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function Metric({ title, value, icon }) {
  return (
    <article style={metricCard}>
      <div style={metricIcon}>{icon}</div>
      <span style={metricTitle}>{title}</span>
      <strong style={metricValue}>{value}</strong>
    </article>
  );
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: "18px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "12px" };
const saveButton = { height: "46px", padding: "0 20px", border: 0, background: "#316ce8", color: "#fff", borderRadius: "12px", display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, cursor: "pointer" };
const heroCard = { background: "linear-gradient(90deg, #2563eb, #4f46e5)", color: "#fff", borderRadius: "22px", padding: "24px", boxShadow: "0 14px 34px rgba(49,108,232,0.18)" };
const heroHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" };
const heroTitle = { fontSize: "18px", fontWeight: 800 };
const liveBadge = { background: "rgba(187,247,208,0.24)", color: "#d1fae5", padding: "8px 14px", borderRadius: "12px", fontWeight: 700 };
const heroGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "12px" };
const metricCard = { background: "rgba(255,255,255,0.08)", padding: "16px", borderRadius: "0", display: "grid", gap: "8px" };
const metricIcon = { opacity: 0.9 };
const metricTitle = { fontSize: "13px", opacity: 0.85 };
const metricValue = { fontSize: "18px", fontWeight: 800 };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "22px", display: "grid", gap: "16px" };
const cardTitle = { margin: 0, fontSize: "16px", fontWeight: 800 };
const passwordButton = { height: "48px", padding: "0 18px", border: 0, background: "#111827", color: "#fff", borderRadius: "12px", fontWeight: 700, cursor: "pointer", justifySelf: "start" };
const heavySettingsLink = { height: "46px", padding: "0 18px", border: 0, background: "#0f172a", color: "#fff", borderRadius: "12px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, textDecoration: "none", justifySelf: "start" };
const fieldWrap = { display: "grid", gap: "8px" };
const fieldLabel = { color: "#334155", fontSize: "13px", fontWeight: 700 };
const fieldInput = { height: "54px", border: "1px solid #cbd5e1", borderRadius: "0", padding: "0 16px", fontSize: "16px" };
const helperText = { margin: "-4px 0 0", color: "#64748b", fontSize: "12px", lineHeight: 1.45 };
const supportPreview = { display: "flex", alignItems: "center", gap: "14px", padding: "14px", borderRadius: "16px", background: "#f0fdf4", border: "1px solid #bbf7d0" };
const supportPreviewIcon = { width: "58px", height: "58px", borderRadius: "18px", background: "linear-gradient(135deg,#25D366 0%, #17b857 100%)", border: "2px solid rgba(255,255,255,0.96)", display: "grid", placeItems: "center", boxShadow: "0 12px 22px rgba(34,197,94,0.18), 0 4px 10px rgba(15,23,42,0.08)" };
const supportHint = { margin: "4px 0 0", color: "#64748b", fontSize: "13px", lineHeight: 1.45 };

export default AdminSettings;
